<template>
  <div class="listen-page">
    <NavBar active-page="listen" />
    
    <div v-if="!isActivated && isAuthenticated" class="activation-banner">
      <div class="banner-content">
        <span>除第一课外，所有课程均需激活后才能访问。</span>
        <button class="activate-button" @click="showActivationModal = true">立即激活</button>
      </div>
    </div>
    
    <div v-if="!isAuthenticated" class="activation-banner">
      <div class="banner-content">
        <span>请登录后使用完整功能。</span>
        <button class="activate-button" @click="navigateToLogin">立即登录</button>
      </div>
    </div>
    
    <div class="content">
      <CourseList :course-data="courseData" :is-activated="isActivated" />
    </div>
    
    <!-- 激活弹窗 -->
    <div v-if="showActivationModal" class="activation-modal">
      <div class="modal-overlay" @click="showActivationModal = false"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3>课程激活</h3>
          <span class="close-button" @click="showActivationModal = false">×</span>
        </div>
        
        <div v-if="isActivated" class="activation-success">
          <div class="success-icon">✓</div>
          <h3>您的课程已成功激活</h3>
          <p>现在您可以访问所有课程内容</p>
          <button class="action-button" @click="showActivationModal = false">确定</button>
        </div>
        
        <div v-else-if="!isAuthenticated" class="login-required">
          <p>请先登录后再激活课程</p>
          <button class="action-button" @click="navigateToLogin">立即登录</button>
        </div>
        
        <div v-else class="activation-form">
          <p class="activation-description">请输入您的激活码以解锁所有课程</p>
          
          <div class="input-group">
            <input
              type="text"
              v-model="activationCode"
              placeholder="请输入激活码"
              :class="{ 'error': activationError }"
            />
            <span v-if="activationError" class="error-message">{{ activationErrorMessage }}</span>
          </div>
          
          <button class="action-button" @click="activateCourses" :disabled="activationLoading">
            {{ activationLoading ? '激活中...' : '激活课程' }}
          </button>
          
          <p class="admin-link">
            如需获取激活码，请联系管理员
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import CourseList from '@/components/CourseList.vue'
import NavBar from '@/components/NavBar.vue'

export default {
  name: 'ListenPage',
  components: {
    CourseList,
    NavBar
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const showActivationModal = ref(false)
    const activationCode = ref('')
    const activationError = ref(false)
    const activationErrorMessage = ref('')
    const activationLoading = ref(false)
    
    const isActivated = computed(() => store.getters.isActivated)
    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    const isAdmin = computed(() => store.getters.isAdmin)
    const currentUser = computed(() => store.getters.currentUser)
    
    const courseData = [
      {
        title: '治国之道',
        lessons: [
          '王者风范',
          '治国方略',
          '国防战争',
          '商业金融',
          '法律制度',
          '工业制造',
          '农业种植'
        ]
      },
      {
        title: '历史民俗',
        lessons: [
          '巾帼须眉',
          '孤家寡人',
          '乱臣贼子',
          '动物世界',
          '水果植物',
          '金属元素'
        ]
      },
      {
        title: '科学文化',
        lessons: [
          '环球旅行',
          '寒窗苦读',
          '宗教哲学',
          '几何代数',
          '疾病生理',
          '情绪动作',
          '文艺出版',
          '象声词汇'
        ]
      },
      {
        title: '宇宙万象',
        lessons: [
          '中华历史',
          '籍贯姓氏',
          '婚丧嫁娶',
          '亲情聚散',
          '世外桃源',
          '市井小镇',
          '烹饪佳肴'
        ]
      }
    ]
    
    const navigateToWatch = () => {
      router.push('/watch')
    }
    
    const navigateToAbout = () => {
      router.push('/about')
    }
    
    const navigateToLogin = () => {
      if (showActivationModal.value) {
        showActivationModal.value = false
      }
      router.push('/login')
    }
    
    const navigateToRegister = () => {
      router.push('/register')
    }
    
    const navigateToAdmin = () => {
      router.push('/admin')
    }
    
    const handleLogout = () => {
      store.commit('logout')
    }
    
    const activateCourses = async () => {
      if (!activationCode.value.trim()) {
        activationError.value = true
        activationErrorMessage.value = '激活码不能为空'
        return
      }
      
      if (!isAuthenticated.value) {
        navigateToLogin()
        return
      }
      
      activationLoading.value = true
      activationError.value = false
      
      try {
        const result = await store.dispatch('verifyActivationCode', activationCode.value)
        if (!result) {
          activationError.value = true
          activationErrorMessage.value = '激活码无效，请重试'
        } else {
          activationError.value = false
          // 激活成功自动关闭弹窗
          setTimeout(() => {
            showActivationModal.value = false
          }, 2000)
        }
      } catch (error) {
        console.error('激活失败:', error)
        activationError.value = true
        
        if (error.response && error.response.data) {
          activationErrorMessage.value = error.response.data.message || '激活失败，请重试'
        } else {
          activationErrorMessage.value = '服务器错误，请稍后再试'
        }
      } finally {
        activationLoading.value = false
      }
    }
    
    onMounted(() => {
      store.dispatch('checkAuth')
    })
    
    return {
      showActivationModal,
      activationCode,
      activationError,
      activationErrorMessage,
      activationLoading,
      courseData,
      isActivated,
      isAuthenticated,
      isAdmin,
      currentUser,
      navigateToWatch,
      navigateToAbout,
      navigateToLogin,
      navigateToRegister,
      navigateToAdmin,
      handleLogout,
      activateCourses
    }
  }
}
</script>

<style scoped>
.listen-page {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #fff;
}

.nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.nav-items {
  display: flex;
  gap: 40px;
}

.nav-item {
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  position: relative;
}

.nav-item.active {
  font-weight: bold;
}

.nav-item.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #000;
}

.auth-buttons {
  display: flex;
  gap: 20px;
}

.auth-button {
  cursor: pointer;
  font-size: 16px;
}

.admin-button {
  color: #e74c3c;
  font-weight: bold;
}

.activation-banner {
  background-color: #f8f9fa;
  padding: 12px 24px;
  border-bottom: 1px solid #eee;
}

.banner-content {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.activate-button {
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
}

.content {
  flex: 1;
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.activation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  width: 400px;
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.close-button {
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.close-button:hover {
  color: #000;
}

.activation-description {
  margin-bottom: 20px;
  color: #333;
}

.input-group {
  margin-bottom: 16px;
}

input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

input.error {
  border-color: #ff4757;
}

.error-message {
  display: block;
  color: #ff4757;
  margin-top: 6px;
  font-size: 14px;
}

.action-button {
  width: 100%;
  padding: 10px;
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
}

.action-button:hover {
  background-color: #1e293b;
}

.action-button:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
}

.activation-success {
  text-align: center;
  padding: 20px 0;
}

.success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #4cd137;
  color: white;
  font-size: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.login-required {
  text-align: center;
  padding: 20px 0;
}

.admin-link {
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #666;
}

.admin-link a {
  color: #2c3e50;
  cursor: pointer;
}

.admin-link a:hover {
  text-decoration: underline;
}
</style> 